/**
 * GitHub Service - Client-side API wrapper
 * Calls the backend API server which handles GitHub authentication
 */

import { supabase } from '@/lib/supabase';

const API_BASE = '';

export interface GitHubRepo {
  id: number;
  name: string;
  full_name: string;
  description: string | null;
  html_url: string;
  default_branch: string;
  language: string | null;
  updated_at: string;
  private: boolean;
  owner: {
    login: string;
    avatar_url: string;
  };
}

export interface GitHubBranch {
  name: string;
  commit: {
    sha: string;
  };
}

export interface GitHubFile {
  name: string;
  path: string;
  type: 'file' | 'dir';
  sha: string;
  size?: number;
}

export interface GitHubFileContent {
  content: string;
  name: string;
  path: string;
  sha: string;
}

export interface GitHubTreeItem {
  path: string;
  type: 'blob' | 'tree';
  size?: number;
  sha: string;
}

export interface GitHubTree {
  sha: string;
  truncated: boolean;
  tree: GitHubTreeItem[];
  totalFiles: number;
  totalDirs: number;
}

export interface GitHubRepoInfo {
  name: string;
  full_name: string;
  description: string | null;
  language: string | null;
  default_branch: string;
  topics: string[];
  visibility: string;
  html_url: string;
  created_at: string;
  updated_at: string;
  pushed_at: string;
  size: number;
  stargazers_count: number;
  forks_count: number;
  open_issues_count: number;
  license: string | null;
}

export interface GitHubDependencies {
  nodejs?: {
    name: string;
    version: string;
    description: string;
    dependencies: Record<string, string>;
    devDependencies: Record<string, string>;
    scripts: Record<string, string>;
  };
  python?: {
    packages: string[];
  };
  pythonProject?: { raw: string };
  rust?: { raw: string };
  go?: { raw: string };
}

// Helper to get auth headers (uses centralized session - single auth call on load)
async function getAuthHeaders(): Promise<HeadersInit> {
  const { getCurrentSession } = await import('@/lib/supabase');
  const session = await getCurrentSession();
  if (!session) {
    throw new Error('Not authenticated. Please log in to access GitHub repositories.');
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
  };
}

/** Parse error body from a failed response (JSON or HTML). */
async function parseErrorResponse(response: Response, fallback: string): Promise<string> {
  const text = await response.text();
  try {
    const data = JSON.parse(text);
    return (data.error ?? data.message ?? fallback) as string;
  } catch {
    return text.slice(0, 200) || response.statusText || fallback;
  }
}

export async function listRepositories(): Promise<GitHubRepo[]> {
  console.log('[GitHub] Fetching repositories...');
  
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/github/repos`, { headers });
  
  if (!response.ok) {
    throw new Error(await parseErrorResponse(response, 'Failed to fetch repositories'));
  }
  
  return response.json();
}

export async function listBranches(owner: string, repo: string): Promise<GitHubBranch[]> {
  console.log(`[GitHub] Fetching branches for ${owner}/${repo}...`);
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/github/repos/${owner}/${repo}/branches`, { headers });
  
  if (!response.ok) {
    throw new Error(await parseErrorResponse(response, 'Failed to fetch branches'));
  }
  
  return response.json();
}

export async function listContents(
  owner: string, 
  repo: string, 
  path: string = '', 
  ref?: string
): Promise<GitHubFile[]> {
  console.log(`[GitHub] Fetching contents for ${owner}/${repo}/${path}...`);
  const params = new URLSearchParams();
  // Use filePath (not path) — Vercel rewrites already set ?path=owner/repo/contents
  if (path) params.set('filePath', path);
  if (ref) params.set('ref', ref);
  
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/api/github/repos/${owner}/${repo}/contents?${params.toString()}`,
    { headers }
  );
  
  if (!response.ok) {
    throw new Error(await parseErrorResponse(response, 'Failed to fetch contents'));
  }
  
  const data = await response.json();
  return Array.isArray(data) ? data : [data];
}

export async function getFileContent(
  owner: string,
  repo: string,
  path: string,
  ref?: string
): Promise<GitHubFileContent> {
  console.log(`[GitHub] Fetching file content for ${owner}/${repo}/${path}...`);
  // Use filePath (not path) — Vercel rewrites already set ?path=owner/repo/file
  const params = new URLSearchParams({ filePath: path });
  if (ref) params.set('ref', ref);
  
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/api/github/repos/${owner}/${repo}/file?${params.toString()}`,
    { headers }
  );
  
  if (!response.ok) {
    throw new Error(await parseErrorResponse(response, 'Failed to fetch file content'));
  }
  
  return response.json();
}

export async function getRepoTree(
  owner: string,
  repo: string,
  ref?: string
): Promise<GitHubTree> {
  console.log(`[GitHub] Fetching tree for ${owner}/${repo}...`);
  const params = new URLSearchParams();
  if (ref) params.set('ref', ref);
  
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/api/github/repos/${owner}/${repo}/tree?${params.toString()}`,
    { headers }
  );
  
  if (!response.ok) {
    throw new Error(await parseErrorResponse(response, 'Failed to fetch tree'));
  }
  
  return response.json();
}

export async function getRepoInfo(owner: string, repo: string): Promise<GitHubRepoInfo> {
  console.log(`[GitHub] Fetching repo info for ${owner}/${repo}...`);
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/github/repos/${owner}/${repo}/info`, { headers });
  
  if (!response.ok) {
    throw new Error(await parseErrorResponse(response, 'Failed to fetch repo info'));
  }
  
  return response.json();
}

export async function getDependencies(
  owner: string,
  repo: string,
  ref?: string
): Promise<GitHubDependencies> {
  console.log(`[GitHub] Fetching dependencies for ${owner}/${repo}...`);
  const params = new URLSearchParams();
  if (ref) params.set('ref', ref);
  
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/api/github/repos/${owner}/${repo}/dependencies?${params.toString()}`,
    { headers }
  );
  
  if (!response.ok) {
    throw new Error(await parseErrorResponse(response, 'Failed to fetch dependencies'));
  }
  
  return response.json();
}

export async function getReadme(
  owner: string,
  repo: string,
  ref?: string
): Promise<{ name: string; path: string; content: string; html_url: string }> {
  console.log(`[GitHub] Fetching README for ${owner}/${repo}...`);
  const params = new URLSearchParams();
  if (ref) params.set('ref', ref);
  
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/api/github/repos/${owner}/${repo}/readme?${params.toString()}`,
    { headers }
  );
  
  if (!response.ok) {
    throw new Error(await parseErrorResponse(response, 'Failed to fetch README'));
  }
  
  return response.json();
}

export async function getLanguages(
  owner: string,
  repo: string
): Promise<Record<string, number>> {
  console.log(`[GitHub] Fetching languages for ${owner}/${repo}...`);
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/github/repos/${owner}/${repo}/languages`, { headers });
  
  if (!response.ok) {
    throw new Error(await parseErrorResponse(response, 'Failed to fetch languages'));
  }
  
  return response.json();
}

export async function batchGetFiles(
  owner: string,
  repo: string,
  paths: string[],
  ref?: string
): Promise<Record<string, { content: string; size: number } | { error: string }>> {
  console.log(`[GitHub] Batch fetching ${paths.length} files for ${owner}/${repo}...`);
  
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/api/github/repos/${owner}/${repo}/batch-files`,
    {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({ paths, ref }),
    }
  );
  
  if (!response.ok) {
    throw new Error(await parseErrorResponse(response, 'Failed to batch fetch files'));
  }
  
  return response.json();
}

// Combined analysis function for agents
export async function getRepoAnalysisContext(
  owner: string,
  repo: string
): Promise<{
  info: GitHubRepoInfo;
  tree: GitHubTree;
  dependencies: GitHubDependencies;
  languages: Record<string, number>;
  readme?: string;
  allFiles?: Record<string, { content: string; size: number }>;
}> {
  console.log(`[GitHub] Fetching full analysis context for ${owner}/${repo}...`);
  
  const [info, tree, dependencies, languages] = await Promise.all([
    getRepoInfo(owner, repo),
    getRepoTree(owner, repo),
    getDependencies(owner, repo),
    getLanguages(owner, repo),
  ]);
  
  let readme: string | undefined;
  try {
    const readmeData = await getReadme(owner, repo);
    readme = readmeData.content;
  } catch (e) {
    // No README
  }
  
  // Fetch ALL files from the repository
  console.log(`[GitHub] Fetching all files from repository...`);
  const allFiles: Record<string, { content: string; size: number }> = {};
  
  try {
    // Get all file paths from the tree — apply same whitelist/blocklist as the RAG pipeline
    const ALLOW_EXT_GIT = new Set([
      '.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs',
      '.py', '.go', '.rs', '.rb', '.java', '.kt',
      '.md', '.mdx', '.json', '.yaml', '.yml',
      '.sh', '.css', '.scss', '.html', '.sql',
    ]);
    const SKIP_DIRS_GIT = new Set([
      'node_modules', 'dist', 'build', '.git', '.next', '.nuxt',
      'coverage', '__pycache__', '.cache', 'vendor', 'out',
      '.turbo', '.vercel', '.svelte-kit',
    ]);
    const SKIP_FILES_GIT = new Set([
      'package-lock.json', 'yarn.lock', 'pnpm-lock.yaml', 'bun.lockb',
    ]);
    const allFilePaths = tree.tree
      .filter(item => {
        if (item.type !== 'blob') return false;
        const lower = item.path.toLowerCase();
        const segments = lower.split('/');
        if (segments.some((s: string) => SKIP_DIRS_GIT.has(s))) return false;
        const filename = segments[segments.length - 1];
        if (SKIP_FILES_GIT.has(filename)) return false;
        const dot = filename.lastIndexOf('.');
        const ext = dot >= 0 ? filename.slice(dot) : '';
        return ALLOW_EXT_GIT.has(ext);
      })
      .map(item => item.path);
    
    console.log(`[GitHub] Found ${allFilePaths.length} files to fetch`);
    
    // Fetch files in batches to avoid overwhelming the API
    const batchSize = 20; // GitHub API can handle this many concurrent requests
    const maxFileSize = 100000; // Skip files larger than 100KB to avoid token limits
    
    for (let i = 0; i < allFilePaths.length; i += batchSize) {
      const batch = allFilePaths.slice(i, i + batchSize);
      console.log(`[GitHub] Fetching batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(allFilePaths.length / batchSize)} (${batch.length} files)...`);
      
      try {
        const batchResults = await batchGetFiles(owner, repo, batch);
        
        // Process results
        for (const [path, result] of Object.entries(batchResults)) {
          if ('content' in result) {
            // Only include files that are not too large
            if (result.size < maxFileSize) {
              allFiles[path] = result as { content: string; size: number };
            } else {
              console.log(`[GitHub] Skipping large file: ${path} (${result.size} bytes)`);
            }
          } else if ('error' in result) {
            console.warn(`[GitHub] Error fetching file ${path}:`, result.error);
          }
        }
      } catch (batchError) {
        console.error(`[GitHub] Error fetching batch:`, batchError);
        // Continue with next batch
      }
      
      // Small delay between batches to be respectful to the API
      if (i + batchSize < allFilePaths.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    console.log(`[GitHub] Successfully fetched ${Object.keys(allFiles).length} files`);
  } catch (error) {
    console.error(`[GitHub] Error fetching all files:`, error);
    // Continue without files - better to have partial context than none
  }
  
  return { info, tree, dependencies, languages, readme, allFiles: Object.keys(allFiles).length > 0 ? allFiles : undefined };
}
